export const ACCESS_FEATURES = [
    { key: 'project_overview', label: 'Project Overview' },
    { key: 'dataroom', label: 'VDR' },
    { key: 'observations', label: 'Observations' },
    { key: 'information_requests', label: 'Info Requests' },
    { key: 'lens_intelligence', label: 'Lens Intelligence' },
    { key: 'project_management', label: 'Project Mgmt.' },
    { key: 'raci_matrix', label: 'RACI Matrix' },
    { key: 'raid_log', label: 'RAID Log' },
    { key: 'action_log', label: 'Action Log' },
    { key: 'lessons_learnt', label: 'Lessons Learnt' },
    { key: 'journal', label: 'Journal' },
    { key: 'the_tent', label: 'The Tent' },
    { key: 'dataroom_wizard', label: 'Wizard' },
    { key: 'maturity_index', label: 'Maturity Index' },
    { key: 'security_index', label: 'Security Index' },
    { key: 'team_analyser', label: 'Team Analyser' },
    { key: 'security_governance', label: 'Security Governance' },
    { key: 'software_inventory', label: 'Software Inventory' },
    { key: 'hardware_inventory', label: 'Hardware Inventory' },
    { key: 'ticket_management', label: 'Ticket Management' },
];

export const REQUIRED_FEATURE_KEYS = ['project_overview', 'dataroom'];

export const DEFAULT_FEATURE_KEYS = ['project_overview', 'dataroom', 'information_requests', 'project_management'];

export function normalizeFeatureKeys(featureKeys) {
    const selected = new Set(featureKeys || []);
    REQUIRED_FEATURE_KEYS.forEach(key => selected.add(key));
    if (selected.has('swot')) selected.add('observations');
    if (selected.has('tasks')) selected.add('project_management');
    selected.delete('swot');
    selected.delete('tasks');
    return ACCESS_FEATURES.map(item => item.key).filter(key => selected.has(key));
}

export const VDR_PRESETS = [
    {
        key: 'editor',
        title: 'Edit files & folders',
        can: 'Can: View files · Add files and folders · Delete files and folders · Download original files',
        mapping: 'Editor',
        permissions: {
            view_perm: true,
            add_perm: true,
            delete_perm: true,
            download_watermarked: false,
            download_original: true,
        },
    },
    {
        key: 'contributor',
        title: 'Add files & folders',
        can: 'Can: View files · Add files and folders · Download original files',
        mapping: 'Contributor',
        permissions: {
            view_perm: true,
            add_perm: true,
            delete_perm: false,
            download_watermarked: false,
            download_original: true,
        },
    },
    {
        key: 'reviewerPlus',
        title: 'View + download original files',
        can: 'Can: View files · Download original files',
        mapping: 'Reviewer Plus',
        permissions: {
            view_perm: true,
            add_perm: false,
            delete_perm: false,
            download_watermarked: false,
            download_original: true,
        },
    },
    {
        key: 'reviewer',
        title: 'View + download watermarked files',
        can: 'Can: View files · Download watermarked copies',
        mapping: 'Reviewer',
        permissions: {
            view_perm: true,
            add_perm: false,
            delete_perm: false,
            download_watermarked: true,
            download_original: false,
        },
    },
    {
        key: 'previewer',
        title: 'View only',
        can: 'Can: View files',
        mapping: 'Previewer',
        permissions: {
            view_perm: true,
            add_perm: false,
            delete_perm: false,
            download_watermarked: false,
            download_original: false,
        },
    },
];

export const createEmptyAccessDraft = () => ({
    name: '',
    features: [...DEFAULT_FEATURE_KEYS],
    workstreamMode: 'selected',
    selectedWorkstreamIds: [],
    intelligence: false,
    projectMail: false,
    projectMeetings: false,
    vdrPreset: 'reviewerPlus',
});

export function presetFromPermissions(permissions) {
    if (!permissions) return 'reviewerPlus';
    if (permissions.delete_perm) return 'editor';
    if (permissions.add_perm) return 'contributor';
    if (permissions.download_original) return 'reviewerPlus';
    if (permissions.download_watermarked) return 'reviewer';
    if (permissions.view_perm) return 'previewer';
    return 'reviewerPlus';
}

export function featureSummary(featureKeys) {
    const selected = new Set(normalizeFeatureKeys(featureKeys));
    return ACCESS_FEATURES.filter(item => selected.has(item.key))
        .map(item => item.label)
        .join(' · ');
}

export function workstreamSummary(draft, workstreams) {
    if (draft?.workstreamMode === 'all') return 'All workstreams';
    const names = (workstreams || [])
        .filter(ws => (draft?.selectedWorkstreamIds || []).includes(ws.id))
        .map(ws => ws.name);
    return names.length ? names.join(' · ') : 'None selected';
}

export function vdrPresetTitle(key) {
    return VDR_PRESETS.find(item => item.key === key)?.title || '';
}

export function groupAccessLines(extra, workstreams) {
    const features = featureSummary(extra?.features);
    const workstreamsLabel = workstreamSummary(extra, workstreams);
    const parts = [workstreamsLabel, features].filter(Boolean);
    return {
        headline: parts.join(' · '),
        vdr: `VDR: ${vdrPresetTitle(extra?.vdrPreset)}`,
        intelligence: `Intelligence Insights: ${extra?.intelligence ? 'Access granted' : 'No access'}`,
        projectMail: `Project Mail: ${extra?.projectMail ? 'Access granted' : 'No access'}`,
        projectMeetings: `Project Meetings: ${extra?.projectMeetings ? 'Access granted' : 'No access'}`,
        features,
        workstreamsLabel,
        vdrTitle: vdrPresetTitle(extra?.vdrPreset),
        intelligenceLabel: extra?.intelligence ? 'Access granted' : 'No access',
        projectMailLabel: extra?.projectMail ? 'Access granted' : 'No access',
        projectMeetingsLabel: extra?.projectMeetings ? 'Access granted' : 'No access',
    };
}

export function isAdminGroup(group) {
    const name = (group?.name || '').trim().toLowerCase();
    return (
        name === 'admin' ||
        name === 'admins' ||
        name === 'administrator' ||
        name === 'administrators' ||
        name === 'project administrator' ||
        name === 'project administrators'
    );
}

export function aclIdsForDraft(draft, workstreams) {
    const list = workstreams || [];
    const selected =
        draft?.workstreamMode === 'all'
            ? list
            : list.filter(ws => (draft?.selectedWorkstreamIds || []).includes(ws.id));
    return selected.map(ws => ws.acl).filter(acl => acl !== undefined && acl !== null);
}

export function storedFeatureKeys(group) {
    return Array.isArray(group?.feature_keys) ? normalizeFeatureKeys(group.feature_keys) : null;
}

export function draftFromGroup(group, workstreams) {
    const aclSet = new Set((group?.acl || []).map(id => String(id)));
    const matched = (workstreams || []).filter(ws => aclSet.has(String(ws.acl)));
    const allSelected = Boolean(group?.all_workstreams);
    const stored = storedFeatureKeys(group);
    return {
        name: group?.name || '',
        features: stored || [...DEFAULT_FEATURE_KEYS],
        workstreamMode: allSelected ? 'all' : 'selected',
        selectedWorkstreamIds: matched.map(ws => ws.id),
        intelligence: Boolean(group?.permissions?.intelligence_insights),
        projectMail: Boolean(group?.permissions?.project_mail),
        projectMeetings: Boolean(group?.permissions?.project_meetings),
        vdrPreset: presetFromPermissions(group?.permissions),
    };
}
